import { useEffect, useState } from 'react';
import { useNoticeToast } from '../lib/toast';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { DataTable, ErrorBox, Pager, usePageSize, dash, money, moneyInput, useList, wallClock, type Column, type Row, IdCell, MoneyInput } from '../components/DataTable';
import { canScreen, useMe } from '../lib/me';
import { useStickyDriveMon } from '../lib/driveMon';
import Modal from '../components/Modal';
import { useAutoSearch } from '../lib/search';

/**
 * 월정기 **정기항목**(템플릿) 관리 — 레거시 `ResultMoDef`.
 *
 * `/result-mo`(월정기)가 "이 달에 무엇을 등록할까"라면 여기는 **무엇을 등록할 수 있게 할까**를 정한다.
 * 매월 3,100~3,300건이 이 목록에서 복사돼 그 달 명세로 나간다.
 *
 * ⚠️ **금액 0 은 휴면**이다. 레거시 목록 SP 가 0원 행을 아예 숨겨서 고를 수 없었고, 실제로도 적용되지 않는다
 *    (2026-06 적용 3,231건 중 0원 템플릿 출신 0건). 우리는 **숨기지 않고 표시**한다 — 숨기면 왜 안 걸리는지도 같이 숨는다.
 *
 * ⚠️ **중복은 대부분 정상이다.** 같은 대상·계정이 두 줄인 것은 월 중 소속·차량이 바뀐 사람의 일할 분할이다
 *    (기본급 1,558,000 + 902,000 = 2,460,000). 그래서 막지 않고 확인만 받는다.
 *
 * ⚠️ 템플릿은 **어느 달에도 속하지 않아** 마감 게이트가 없다. 대신 고쳐도 이미 만들어진 그 달 항목은 안 바뀐다.
 */


/** 계정 선택 — 지급/공제 축이 다르다. 마스터 밖 과거 값도 유지한다(레거시 자유입력 잔재). */
export function AccountField({
  isPlus, value, onChange,
}: { isPlus: boolean; value: string; onChange: (v: string) => void }) {
  const [opts, setOpts] = useState<string[]>([]);
  useEffect(() => {
    let alive = true;
    api.listMoAccountNames({ isPlus })
      .then((r) => { if (alive) setOpts(r.rows.map((x) => x.account_name)); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [isPlus]);

  return (
    <select className="field" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">계정 선택</option>
      {value && !opts.includes(value) && <option value={value}>{value} ⚠ 미등록</option>}
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export interface Target {
  driverId: number; carId: number; ownerId: number; grpId: number; label: string;
}

/**
 * 대상 고르기 — **편성으로 고른다.** 네 칸(기사·차량·차주·소속)을 따로 고르게 하면
 * 실재하지 않는 조합이 만들어진다. 실측상 활성 템플릿 3,973건의 대상이 전부 실재 편성과 일치한다.
 */
export function TargetPicker({ onPick }: { onPick: (t: Target) => void }) {
  const [term, setTerm] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);

  async function search(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.listMoDefTargets({ q: term.trim() || undefined, limit: 30 });
      setRows(r.rows as unknown as Row[]);
      setSearched(true);
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-2 rounded-lg border border-line bg-canvas p-3">
      <form onSubmit={search} className="flex items-center gap-2">
        <input className="field w-64" placeholder="기사·차량번호·차주·소속으로 검색"
               value={term} onChange={(e) => setTerm(e.target.value)} />
        <button className="btn-ghost" type="submit" disabled={busy}>편성 찾기</button>
      </form>

      {searched && rows.length === 0 && (
        <p className="text-xs text-ink-faint">{busy ? '불러오는 중…' : '운용중 편성에서 찾지 못했습니다.'}</p>
      )}

      {rows.length > 0 && (
        <ul className="max-h-56 space-y-1 overflow-y-auto text-sm">
          {rows.map((r) => (
            <li key={String(r.car_alloc_id)}>
              <button
                type="button"
                className="w-full rounded px-2 py-1 text-left hover:bg-surface"
                onClick={() => onPick({
                  driverId: Number(r.driver_id), carId: Number(r.car_id),
                  ownerId: Number(r.owner_id), grpId: Number(r.grp_id),
                  label: `편성 ID ${r.car_alloc_id} · ${dash(r.driver_name)} · ${dash(r.car_no)} · ${dash(r.owner_name)} · ${dash(r.grp_name)}`,
                })}
              >
                {/* #433 같은 사람·차량 편성이 여럿이면 목록의 「편성 ID」 칸과 맞춰 고른다. */}
                <span className="mr-2 text-xs tabular-nums text-ink-soft">편성 ID {String(r.car_alloc_id)}</span>
                <b>{dash(r.driver_name)}</b> · {dash(r.car_no)}
                <span className="ml-2 text-ink-faint">{dash(r.owner_name)} · {dash(r.grp_name)}</span>
                <span className="ml-2 text-xs text-ink-faint">
                  {String(r.startdt ?? '').slice(0, 10)}~{String(r.enddt ?? '') ? String(r.enddt).slice(0, 10) : ''}
                  {Number(r.def_count) > 0 && ` · 정기항목 ${r.def_count}건`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* 편성은 유일하지 않다(차량 1대당 최대 205건) — 기간을 보고 고르라고 알린다. */}
      <p className="text-xs text-ink-faint">같은 사람·차량의 편성이 여럿일 수 있습니다. 기간을 보고 고르세요.</p>
    </div>
  );
}

/** 등록 창 한 줄 — 편성 하나에 계정별로 여러 줄을 한 번에 넣는다(#402 · 레거시 버스인처럼). */
interface DefLine { key: number; isPlus: boolean; accountName: string; amt: string; comment: string }

let lineSeq = 0;
function newLine(isPlus = false): DefLine {
  lineSeq += 1;
  return { key: lineSeq, isPlus, accountName: '', amt: '', comment: '' };
}

export function DefForm({
  row, onClose, onSaved,
}: { row: Row | 'new'; onClose: () => void; onSaved: (msg: string) => void }) {
  const editing = row !== 'new';
  const r = editing ? row : null;

  const [target, setTarget] = useState<Target | null>(
    editing
      ? {
          driverId: Number(r!.driver_id), carId: Number(r!.car_id),
          ownerId: Number(r!.owner_id), grpId: Number(r!.grp_id),
          label: `${dash(r!.driver_name)} · ${dash(r!.car_no)} · ${dash(r!.owner_name)} · ${dash(r!.grp_name)}`,
        }
      : null,
  );
  // 수정은 한 줄 그대로 · 새 등록은 줄을 늘릴 수 있다(편성 한 번 고르고 계정별로 여러 항목).
  const [lines, setLines] = useState<DefLine[]>(() => [
    editing
      ? { key: 0, isPlus: r!.is_plus === true, accountName: String(r!.account_name ?? ''), amt: moneyInput(r!.amt), comment: String(r!.comment ?? '') }
      : newLine(),
  ]);
  const [saved, setSaved] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 중복 확인은 줄마다 — 그 줄에서 멈추고 「그래도 등록」이면 그 줄부터 이어 간다.
  const [dupWarn, setDupWarn] = useState<{ key: number; text: string } | null>(null);

  function patch(key: number, p: Partial<DefLine>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...p } : l)));
  }

  async function save(confirmKey: number | null = null) {
    if (!target) { setError('대상 편성을 고르세요.'); return; }
    const missing = lines.findIndex((l) => l.accountName === '');
    if (missing >= 0) { setError(`${missing + 1}번째 줄의 계정을 고르세요.`); return; }
    setSaving(true); setError(null);
    let rest = [...lines];
    let done = saved;
    try {
      while (rest.length > 0) {
        const l = rest[0]!;
        const res = await api.saveResultMoDef({
          resultMoDefId: editing ? Number(r!.result_mo_def_id) : undefined,
          ownerId: target.ownerId, driverId: target.driverId, carId: target.carId, grpId: target.grpId,
          accountName: l.accountName, amt: l.amt.trim() === '' ? '0' : l.amt, isPlus: l.isPlus,
          comment: l.comment || undefined,
          confirmDuplicate: confirmKey === l.key,
        });
        if (res.needsConfirm) {
          const list = (res.duplicates ?? []).map((d) => money(d.amt)).join(' · ');
          setDupWarn({
            key: l.key,
            text: `「${l.accountName}」 — ${res.message ?? '같은 대상·계정의 정기항목이 이미 있습니다.'}\n기존 금액: ${list}`,
          });
          setLines(rest); setSaved(done); setSaving(false);
          return;
        }
        rest = rest.slice(1);
        done += 1;
      }
      onSaved(editing ? '정기항목을 수정했습니다.' : `정기항목 ${done.toLocaleString('ko-KR')}건을 등록했습니다.`);
    } catch (e) {
      // 앞 줄은 이미 들어갔다 — 남은 줄만 남겨 다시 누르면 거기서부터.
      setLines(rest); setSaved(done);
      setError((done > saved ? `${done}건은 등록됐습니다. 남은 줄: ` : '') + (e instanceof Error ? e.message : String(e)));
      setSaving(false);
    }
  }

  return (
    <div className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold">정기항목 {editing ? '수정' : '등록'}</h2>
        {saved > 0 && <span className="text-xs text-emerald-700">{saved}건 등록됨</span>}
        <button className="btn-ghost ml-auto" onClick={() => (saved > 0 ? onSaved(`정기항목 ${saved}건을 등록했습니다.`) : onClose())}
                disabled={saving}>
          {saved > 0 ? '닫기' : '취소'}
        </button>
        <button className="btn-primary" onClick={() => void save(null)} disabled={saving || !target}>
          {saving ? '저장 중…' : editing ? '수정' : lines.length > 1 ? `${lines.length}건 등록` : '등록'}
        </button>
      </div>

      {error && <ErrorBox message={error} />}

      {dupWarn && (
        <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <p className="whitespace-pre-line">{dupWarn.text}</p>
          <div className="flex gap-2">
            <button className="btn-ghost text-xs" onClick={() => setDupWarn(null)}>다시 보기</button>
            <button className="btn-primary text-xs" onClick={() => { const k = dupWarn.key; setDupWarn(null); void save(k); }}>
              그래도 등록
            </button>
          </div>
        </div>
      )}

      <div className="space-y-1">
        <span className="text-sm font-medium">대상 편성 {!target && <span className="text-red-600">*</span>}</span>
        {target === null
          ? <TargetPicker onPick={setTarget} />
          : (
            <p className="text-sm">
              {target.label}
              {saved === 0 && (
                <button className="btn-ghost ml-2 text-xs" onClick={() => setTarget(null)}>다시 고르기</button>
              )}
            </p>
          )}
      </div>

      <div className="space-y-2">
        {lines.map((l, i) => {
          const zero = l.amt.trim() === '' || Number(l.amt.replace(/,/g, '')) === 0;
          return (
            <div key={l.key} className={`grid gap-3 md:grid-cols-[8rem_minmax(0,1fr)_10rem_minmax(0,1fr)_auto] ${
              dupWarn?.key === l.key ? 'rounded-lg bg-amber-50 p-1' : ''}`}>
              <label className="block">
                {i === 0 && <span className="mb-1 block text-sm font-medium">구분</span>}
                <select className="field" value={l.isPlus ? 'plus' : 'minus'}
                        onChange={(e) => patch(l.key, { isPlus: e.target.value === 'plus', accountName: '' })}>
                  <option value="minus">공제(−)</option>
                  <option value="plus">지급(+)</option>
                </select>
              </label>
              <label className="block">
                {i === 0 && <span className="mb-1 block text-sm font-medium">계정 <span className="text-red-600">*</span></span>}
                <AccountField isPlus={l.isPlus} value={l.accountName} onChange={(v) => patch(l.key, { accountName: v })} />
              </label>
              <label className="block">
                {i === 0 && <span className="mb-1 block text-sm font-medium">금액</span>}
                <MoneyInput placeholder="0" value={l.amt} onChange={(v) => patch(l.key, { amt: v })} />
                {zero && <span className="mt-1 block text-xs text-amber-700">0 이면 휴면</span>}
              </label>
              <label className="block">
                {i === 0 && <span className="mb-1 block text-sm font-medium">비고</span>}
                <input className="field" value={l.comment} onChange={(e) => patch(l.key, { comment: e.target.value })} />
              </label>
              {!editing && (
                <div className={i === 0 ? 'pt-6' : ''}>
                  <button className="btn-ghost px-2 text-xs" disabled={saving || lines.length === 1}
                          onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} title="이 줄 빼기">
                    ✕
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {!editing && (
          <div className="flex flex-wrap items-center gap-3">
            <button className="btn-ghost text-xs" disabled={saving}
                    onClick={() => setLines((ls) => [...ls, newLine(ls[ls.length - 1]?.isPlus ?? false)])}>
              + 계정 줄 추가
            </button>
            <span className="text-xs text-ink-faint">
              편성을 한 번 고르고 계정별로 여러 줄을 한 번에 등록합니다. 금액 0 은 휴면(목록엔 남고 그 달 등록 대상에서 빠짐).
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/** 선택한 항목들을 한 번에 바꾼다. 세 갈래 전부 변경 전 스냅샷이 이력에 남는다. */
export function BulkPanel({
  picked, onClose, onDone, noAssign = false,
}: {
  picked: number[]; onClose: () => void; onDone: (msg: string) => void;
  /** 월정기 화면(#328) — 대상 재배정은 그 화면 「선택 편성변경」(그 달 등록분까지 옮김)이 맡는다. */
  noAssign?: boolean;
}) {
  const [kind, setKind] = useState<'amt' | 'account' | 'assign'>('amt');
  const [amt, setAmt] = useState('');
  const [accountName, setAccountName] = useState('');
  const [isPlus, setIsPlus] = useState(false);
  const [target, setTarget] = useState<Target | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true); setError(null);
    try {
      const r = await api.bulkUpdateResultMoDef({
        defIds: picked, kind,
        ...(kind === 'amt' ? { amt: amt.trim() === '' ? '0' : amt } : {}),
        ...(kind === 'account' ? { accountName } : {}),
        ...(kind === 'assign' && target
          ? { ownerId: target.ownerId, driverId: target.driverId, carId: target.carId, grpId: target.grpId }
          : {}),
      });
      onDone(
        `${r.updated.toLocaleString('ko-KR')}건 변경`
        + (r.skipped > 0 ? ` · ${r.skipped}건은 이미 삭제돼 건너뜀` : '')
        + ` · 이력 ${r.logged.toLocaleString('ko-KR')}건 기록`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const ready = kind === 'amt' ? amt.trim() !== ''
    : kind === 'account' ? accountName !== ''
    : target !== null;

  return (
    <div className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold">일괄 변경 <span className="text-ink-soft">{picked.length}건 선택</span></h2>
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={busy}>닫기</button>
        <button className="btn-primary" onClick={() => void run()} disabled={busy || !ready}>
          {busy ? '변경 중…' : '변경'}
        </button>
      </div>

      {error && <ErrorBox message={error} />}

      <div className="flex gap-1">
        {([['amt', '금액'], ['account', '계정명'], ['assign', '대상 재배정']] as const)
          .filter(([id]) => !(noAssign && id === 'assign')).map(([id, label]) => (
          <button key={id} onClick={() => setKind(id)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                    kind === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>
            {label}
          </button>
        ))}
      </div>

      {kind === 'amt' && (
        <label className="block w-48">
          <span className="mb-1 block text-sm font-medium">새 금액</span>
          <MoneyInput value={amt} onChange={setAmt} />
          <span className="mt-1 block text-xs text-ink-faint">
            선택한 항목 전부가 이 금액이 됩니다. 0 으로 두면 휴면 처리됩니다.
          </span>
        </label>
      )}

      {kind === 'account' && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="block w-40">
            <span className="mb-1 block text-sm font-medium">계정 축</span>
            <select className="field" value={isPlus ? 'plus' : 'minus'}
                    onChange={(e) => { setIsPlus(e.target.value === 'plus'); setAccountName(''); }}>
              <option value="minus">공제(−)</option>
              <option value="plus">지급(+)</option>
            </select>
          </label>
          <label className="block w-56">
            <span className="mb-1 block text-sm font-medium">새 계정</span>
            <AccountField isPlus={isPlus} value={accountName} onChange={setAccountName} />
          </label>
          {/* 계정명만 바꾼다 — 지급/공제 자체는 안 바꾼다(레거시 `_UpdateAccountName` 과 같다). */}
          <p className="text-xs text-ink-faint">
            계정 <b>이름만</b> 바뀝니다. 지급/공제 구분은 각 항목의 값 그대로입니다.
          </p>
        </div>
      )}

      {kind === 'assign' && (
        <div className="space-y-1">
          <span className="text-sm font-medium">새 대상 편성</span>
          {target === null
            ? <TargetPicker onPick={setTarget} />
            : (
              <p className="text-sm">
                {target.label}
                <button className="btn-ghost ml-2 text-xs" onClick={() => setTarget(null)}>다시 고르기</button>
              </p>
            )}
          <p className="text-xs text-ink-faint">
            기사가 차를 바꾸거나 소속이 옮겨갈 때 그 사람의 정기항목을 한 번에 옮깁니다(레거시 편성 일괄변경).
          </p>
        </div>
      )}
    </div>
  );
}

/** 대상 조합 전체에 항목 하나를 심는다. 미리보기를 **먼저** 부른다. */
function CreateAllPanel({ onClose, onDone }: { onClose: () => void; onDone: (msg: string) => void }) {
  const [isPlus, setIsPlus] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [amt, setAmt] = useState('');
  const [comment, setComment] = useState('');
  const [preview, setPreview] = useState<{ targets: number; toCreate: number; skipped: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(dryRun: boolean) {
    setBusy(true); setError(null);
    try {
      const r = await api.createAllResultMoDef({
        accountName, amt: amt.trim(), isPlus, comment: comment || undefined, dryRun,
      });
      if (dryRun) {
        setPreview({ targets: r.targets ?? 0, toCreate: r.toCreate ?? 0, skipped: r.skipped ?? 0 });
        setBusy(false);
      } else {
        onDone(`${(r.created ?? 0).toLocaleString('ko-KR')}건 생성`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const ready = accountName !== '' && amt.trim() !== '' && Number(amt.replace(/,/g, '')) !== 0;

  return (
    <div className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold">전체 심기</h2>
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={busy}>닫기</button>
      </div>

      <p className="text-sm text-ink-soft">
        이미 정기항목을 가진 <b>모든 대상</b>에 항목 하나를 추가합니다 — “전 기사에게 상조회비 8,000원” 같은 신설.
        새 사람을 만들지는 않습니다.
      </p>

      {error && <ErrorBox message={error} />}

      <div className="grid gap-4 md:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-sm font-medium">구분</span>
          <select className="field" value={isPlus ? 'plus' : 'minus'}
                  onChange={(e) => { setIsPlus(e.target.value === 'plus'); setAccountName(''); setPreview(null); }}>
            <option value="minus">공제(−)</option>
            <option value="plus">지급(+)</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">계정 <span className="text-red-600">*</span></span>
          <AccountField isPlus={isPlus} value={accountName}
                        onChange={(v) => { setAccountName(v); setPreview(null); }} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">금액 <span className="text-red-600">*</span></span>
          <MoneyInput value={amt} onChange={(v) => { setAmt(v); setPreview(null); }} />
        </label>
      </div>

      <label className="block">
        <span className="mb-1 block text-sm font-medium">비고</span>
        <input className="field" value={comment} onChange={(e) => setComment(e.target.value)} />
      </label>

      {/* 레거시는 0원 심기를 허용했고, 그 결과 같은 행을 매달 다시 만들어 190만 행이 됐다. */}
      <p className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-900">
        금액 0 으로는 심을 수 없습니다. 레거시가 0원으로 심다가 중복검사를 스스로 빠져나가 같은 행을 반복 생성했고,
        그것이 <b>190만 행 중 99.8%</b>의 정체입니다.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <button className="btn-ghost" onClick={() => void run(true)} disabled={busy || !ready}>미리보기</button>
        {preview && (
          <>
            <span className="text-sm">
              대상 <b>{preview.targets.toLocaleString('ko-KR')}</b> ·
              생성 <b className="text-emerald-700">{preview.toCreate.toLocaleString('ko-KR')}</b> ·
              이미 있음 <b>{preview.skipped.toLocaleString('ko-KR')}</b>
            </span>
            <button className="btn-primary" onClick={() => void run(false)}
                    disabled={busy || preview.toCreate === 0}>
              {busy ? '생성 중…' : `${preview.toCreate.toLocaleString('ko-KR')}건 생성`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** 변경 이력 — 남은 스냅샷은 **바뀌기 전** 값이다. */
function LogPanel({ defId, onClose }: { defId: number; onClose: () => void }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.listResultMoDefLog({ resultMoDefId: defId })
      .then((r) => { if (alive) setRows(r.rows as Row[]); })
      .catch(() => undefined)
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [defId]);

  return (
    <div className="card space-y-3 p-5">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-bold">변경 이력</h2>
        <span className="text-sm text-ink-soft">각 줄은 <b>바뀌기 전</b> 값입니다.</span>
        <button className="btn-ghost ml-auto" onClick={onClose}>닫기</button>
      </div>
      {loading
        ? <p className="text-sm text-ink-faint">불러오는 중…</p>
        : rows.length === 0
          ? <p className="text-sm text-ink-faint">변경 이력이 없습니다.</p>
          : (
            <ul className="space-y-1 text-sm">
              {rows.map((l) => (
                <li key={String(l.result_mo_def_update_log_id)} className="flex flex-wrap gap-2 border-b border-line py-1">
                  <span className="text-ink-faint">{wallClock(l.update_log_at)}</span>
                  <span>{dash(l.account_name)}</span>
                  <b>{money(l.amt)}</b>
                  <span className="text-ink-soft">
                    {dash(l.driver_name)} · {dash(l.car_no)} · {dash(l.owner_name)} · {dash(l.grp_name)}
                  </span>
                  <span className="ml-auto text-xs text-ink-faint">{dash(l.updated_by)}</span>
                </li>
              ))}
            </ul>
          )}
    </div>
  );
}

export default function ResultMoDef() {
  const { me } = useMe();
  const canEdit = canScreen(me, 'result-mo');

  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [sign, setSign] = useState<'' | 'plus' | 'minus'>('');
  const [amtFilter, setAmtFilter] = useState<'' | 'dormant' | 'active'>('');
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('result-mo-def', 50);

  // 치는 대로 찾는다(입력이 멎으면 적용). 버튼·Enter 는 그대로 — `lib/search.ts` 참고.
  useAutoSearch(term, (v) => { setQ(v); setPage(0); });
  const [nonce, setNonce] = useState(0);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [panel, setPanel] = useState<'none' | 'form' | 'bulk' | 'createAll' | 'log'>('none');
  const [editRow, setEditRow] = useState<Row | 'new'>('new');
  const [logId, setLogId] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<{
    n: string; plus_n: string; minus_n: string; dormant_n: string; plus_amt: string; minus_amt: string;
  } | null>(null);

  // 이 화면은 월정기(`/result-mo`)에서 들어온다. 거기서 고른 정산년월을 **그대로 물고 온다** —
  // 안 물고 오면 담당자는 방금 보던 달을 잃고, '적용' 열이 어느 달 기준인지도 알 수 없다.
  // 여기에는 월 선택기를 두지 않는다(템플릿은 월 소유가 아니다). 기억된 달을 읽기만 한다.
  const [mon] = useStickyDriveMon();

  const { rows, loading } = useList(async (alive) => {
    const r = await api.listResultMoDef({
      q: q || undefined,
      isPlus: sign === 'plus' ? true : sign === 'minus' ? false : undefined,
      amtFilter: amtFilter || undefined,
      driveMon: mon || undefined,
      limit: PAGE, offset: page * PAGE,
    });
    if (alive()) setTotal(r.total);
    return r as unknown as { rows: Row[] };
  }, [q, sign, amtFilter, page, PAGE, nonce, mon]);

  function refresh(msg?: string) {
    if (msg) setNotice(msg);
    setPicked(new Set());
    setPanel('none');
    setNonce((n) => n + 1);
  }

  async function remove(r: Row) {
    const applied = Number(r.applied_months ?? 0);
    const warn = applied > 0
      ? `이 정기항목은 ${applied}개월에 적용된 적이 있습니다(최근 ${dash(r.last_applied_mon)}).\n`
        + '삭제해도 이미 등록된 그 달 월정기는 남습니다.\n\n'
      : '';
    if (!confirm(`${warn}“${dash(r.account_name)} ${money(r.amt)}”을(를) 삭제할까요?`)) return;
    setError(null);
    try {
      const res = await api.deleteResultMoDef({ resultMoDefId: Number(r.result_mo_def_id) });
      const open = res.openMonthsStillApplied;
      refresh(
        '정기항목을 삭제했습니다.'
        + (open.length > 0
          ? ` ⚠ 아직 마감되지 않은 ${open.join(', ')} 에는 이 항목이 등록된 채 남아 있습니다 — 필요하면 월정기 화면에서 취소하세요.`
          : ''),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function toggle(id: number) {
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  const allOnPage = rows.map((r) => Number(r.result_mo_def_id));
  const allPicked = allOnPage.length > 0 && allOnPage.every((id) => picked.has(id));

  const columns: Column[] = [
    ...(canEdit
      ? [{
          key: '_pick',
          label: (
            <input type="checkbox" checked={allPicked}
                   onChange={() => setPicked((s) => {
                     const n = new Set(s);
                     if (allPicked) allOnPage.forEach((id) => n.delete(id));
                     else allOnPage.forEach((id) => n.add(id));
                     return n;
                   })} />
          ),
          render: (r: Row) => (
            <input type="checkbox" checked={picked.has(Number(r.result_mo_def_id))}
                   onChange={() => toggle(Number(r.result_mo_def_id))}
                   onClick={(e) => e.stopPropagation()} />
          ),
        } as Column]
      : []),
    {
      key: 'is_plus', label: '구분',
      render: (r) => r.is_plus === true
        ? <span className="text-emerald-700">지급</span>
        : <span className="text-red-700">공제</span>,
    },
    { key: 'account_name', label: '계정' },
    {
      key: 'amt', label: '금액', align: 'right',
      render: (r) => (
        <span>
          <b>{money(r.amt)}</b>
          {r.is_dormant === true && (
            <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-ink-soft"
                  title="금액이 0 이라 그 달 등록 대상에서 빠집니다">휴면</span>
          )}
        </span>
      ),
    },
    { key: 'car_alloc_id', label: '편성 ID', align: 'right', render: (r) => <IdCell v={r.car_alloc_id} warn /> },
    { key: 'driver_id', label: '기사 ID', align: 'right', render: (r) => <IdCell v={r.driver_id} /> },
    { key: 'owner_id', label: '차주 ID', align: 'right', render: (r) => <IdCell v={r.owner_id} /> },
    { key: 'driver_name', label: '기사' },
    {
      key: 'car_no', label: '차량',
      render: (r) => (
        <span>
          {dash(r.car_no)}
          {r.car_deleted === true && <span className="ml-1 text-xs text-amber-700" title="삭제된 차량">⚠</span>}
        </span>
      ),
    },
    { key: 'owner_name', label: '차주' },
    { key: 'grp_name', label: '소속' },
    {
      key: 'applied_months', label: '적용', align: 'right',
      render: (r) => Number(r.applied_months ?? 0) === 0
        ? <span className="text-ink-faint" title="한 번도 적용된 적 없음">—</span>
        : <span title={`최근 ${dash(r.last_applied_mon)}`}>{String(r.applied_months)}개월</span>,
    },
    ...(canEdit
      ? [{
          key: '_act', label: '',
          render: (r: Row) => (
            <span className="flex gap-1">
              <button className="btn-ghost text-xs"
                      onClick={(e) => { e.stopPropagation(); setLogId(Number(r.result_mo_def_id)); setPanel('log'); }}>
                이력
              </button>
              <button className="btn-ghost text-xs text-red-600"
                      onClick={(e) => { e.stopPropagation(); void remove(r); }}>
                삭제
              </button>
            </span>
          ),
        } as Column]
      : []),
  ];

  return (
    <div className="space-y-4">
      <div className="page-head">
        {/* 월정기의 하위 화면 — 상단 메뉴에 따로 없으니 돌아갈 길을 여기 둔다(2026-09-28 way). */}
        <Link className="text-sm text-ink-soft hover:text-brand-700" to="/result-mo">← 월정기로 돌아가기</Link>
        <h1 className="font-bold">
          <span className="font-medium text-ink-faint">월정기 › </span>정기항목
        </h1>
        <p className="mt-1 text-sm text-ink-soft">
          월정기의 <b>템플릿</b>입니다. 매월 여기서 골라 그 달 월정기를 만듭니다 —
          여기가 바뀌면 <b>다음에 등록하는 달</b>부터 반영되고, 이미 등록된 달은 그대로입니다.
        </p>
        {/* 템플릿 자체는 어느 달에도 속하지 않는다 — 그래서 월 선택기를 두지 않는다.
            다만 '적용' 열은 달 기준이라 **어느 달을 보고 있는지** 말해 줘야 한다.
            월정기 화면에서 고른 달을 그대로 물고 온다(`lib/driveMon.ts`). */}
        {mon && (
          <p className="mt-1 text-sm text-ink-soft">
            ‘적용’ 열은 <b>{mon}</b> 기준입니다 — 월정기 화면에서 고른 달을 그대로 따릅니다.
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {([['', '전체'], ['minus', '공제'], ['plus', '지급']] as const).map(([id, label]) => (
            <button key={id} onClick={() => { setSign(id); setPage(0); }}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                      sign === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {([['', '금액 전체'], ['active', '유효'], ['dormant', '휴면(0원)']] as const).map(([id, label]) => (
            <button key={id} onClick={() => { setAmtFilter(id); setPage(0); }}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                      amtFilter === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>
              {label}
            </button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); setPage(0); }}
              className="flex items-center gap-2">
          <input className="field w-56" placeholder="계정·기사·차량·차주·소속"
                 value={term} onChange={(e) => setTerm(e.target.value)} />
          <button className="btn-search" type="submit" aria-label="검색" title="검색" />
        </form>
      </div>

      {total && (
        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-line bg-surface px-3 py-2 text-sm">
          <span>정기항목 <b>{Number(total.n).toLocaleString('ko-KR')}</b></span>
          <span className="text-emerald-700">지급 {Number(total.plus_n).toLocaleString('ko-KR')}건 · {money(total.plus_amt)}</span>
          <span className="text-red-700">공제 {Number(total.minus_n).toLocaleString('ko-KR')}건 · {money(total.minus_amt)}</span>
          {Number(total.dormant_n) > 0 && (
            <span className="text-ink-faint" title="금액이 0 이라 그 달 등록 대상에서 빠지는 항목">
              휴면 {Number(total.dormant_n).toLocaleString('ko-KR')}건
            </span>
          )}
        </div>
      )}

      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <button className="btn-primary text-xs" onClick={() => { setEditRow('new'); setPanel('form'); }}>
            새 정기항목
          </button>
          <span className="text-xs text-ink-faint">선택 {picked.size}건</span>
          <button className="btn-ghost text-xs" disabled={picked.size === 0} onClick={() => setPanel('bulk')}>
            일괄 변경
          </button>
          <button className="btn-ghost text-xs" onClick={() => setPanel('createAll')}>전체 심기</button>
        </div>
      )}

      {error && <ErrorBox message={error} />}

      {panel === 'form' && (
        <Modal>
          <DefForm row={editRow} onClose={() => setPanel('none')} onSaved={(m) => refresh(m)} />
        </Modal>
      )}
      {panel === 'bulk' && (
        <Modal>
          <BulkPanel picked={[...picked]} onClose={() => setPanel('none')} onDone={(m) => refresh(m)} />
        </Modal>
      )}
      {panel === 'createAll' && (
        <Modal>
          <CreateAllPanel onClose={() => setPanel('none')} onDone={(m) => refresh(m)} />
        </Modal>
      )}
      {panel === 'log' && logId !== null && (
        <Modal>
          <LogPanel defId={logId} onClose={() => setPanel('none')} />
        </Modal>
      )}

      <DataTable
        tableId="settle.resultMoDef"
        columns={columns}
        rows={rows}
        loading={loading}
        onRowClick={canEdit ? (r) => { setEditRow(r); setPanel('form'); } : undefined}
        activeRow={(r) => panel === 'form' && editRow !== 'new'
          && Number((editRow as Row).result_mo_def_id) === Number(r.result_mo_def_id)}
      />
      <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={50} />

      <p className="text-xs text-ink-faint">
        같은 대상·계정이 두 줄인 것은 대개 정상입니다 — 월 중 소속·차량이 바뀐 사람의 금액을 나눠 둔 것입니다.
        수정·삭제·일괄변경은 모두 변경 전 값이 이력에 남습니다.
      </p>
    </div>
  );
}
